import { useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/api/auth.api';
import { usersApi } from '@/api/users.api';
import { useAuthStore, hasSessionHint } from '@/store/authStore';
import { toast } from '@/store/uiStore';
import { queryKeys } from './queryKeys';
import { getApiErrorMessage, getApiErrorCode } from '@/api/client';
import type { LoginInput, RegisterInput } from '@/types/auth';

/**
 * useAuth — consolidated hooks for login/register/logout/silent-restore.
 *
 * Silent restore behaviour: on mount, attempt `usersApi.getMe()`. If the
 * refresh cookie is valid the response interceptor will mint a fresh access
 * token transparently. If no cookie / cookie expired, the request 401s,
 * the interceptor calls onAuthFailure() → clearAuth() and we land in
 * the unauthenticated state (no redirect — that only happens on a protected
 * action).
 */

// Module-level guard. StrictMode double-invokes useEffect in dev, and Zustand
// selector identity changes can also re-trigger the effect. We only EVER want
// one /auth/refresh attempt per page load — it 401s for anonymous users and
// adds console noise + needless network. Once attempted, never again until
// the user explicitly logs in/out (which directly updates the store).
let silentRestoreInflight: Promise<void> | null = null;
let silentRestoreSettled = false;

export function useSilentRestore(): void {
  const setAuth = useAuthStore((s) => s.setAuth);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const setHydrating = useAuthStore((s) => s.setHydrating);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (silentRestoreSettled) return;
    if (silentRestoreInflight) return;

    // Skip the refresh call entirely when the device has no prior login hint.
    // This eliminates the unavoidable 401 noise for anonymous visitors. The
    // hint is set on successful login and cleared on logout, so it's accurate.
    if (!hasSessionHint()) {
      silentRestoreSettled = true;
      setHydrating(false);
      clearAuth();
      return;
    }

    setHydrating(true);

    silentRestoreInflight = (async () => {
      try {
        const { accessToken } = await authApi.refresh();
        const user = await usersApi.getMe();
        setAuth(accessToken, user);
      } catch {
        clearAuth();
      } finally {
        silentRestoreSettled = true;
        silentRestoreInflight = null;
        setHydrating(false);
        queryClient.invalidateQueries({ queryKey: queryKeys.cart });
      }
    })();
  }, [setAuth, clearAuth, setHydrating, queryClient]);
}

export function useLogin() {
  const setAuth = useAuthStore((s) => s.setAuth);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: LoginInput) => authApi.login(input),
    onSuccess: ({ user, accessToken }) => {
      setAuth(accessToken, user);
      toast.success('Welcome back', user.name);
      queryClient.invalidateQueries({ queryKey: queryKeys.cart });
    },
    onError: (err) => {
      // A locked account is account-wide (not device/location specific) and the
      // backend message includes a raw UTC timestamp. Surface a clear, actionable
      // message instead so it doesn't read like a "device/location" restriction.
      if (getApiErrorCode(err) === 'ACCOUNT_LOCKED') {
        toast.error(
          'Too many attempts',
          'This account is temporarily locked after several wrong passwords. Wait a few minutes, or use "Forgot?" to reset your password — that also unlocks it.',
        );
        return;
      }
      toast.error('Login failed', getApiErrorMessage(err));
    },
  });
}

export function useGoogleAuth() {
  const setAuth = useAuthStore((s) => s.setAuth);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { credential: string }) => authApi.google(input),
    onSuccess: ({ user, accessToken }) => {
      setAuth(accessToken, user);
      toast.success('Welcome', user.name);
      queryClient.invalidateQueries({ queryKey: queryKeys.cart });
    },
    onError: (err) => toast.error('Google sign-in failed', getApiErrorMessage(err)),
  });
}

export function useRegister() {
  // Registration creates an UNVERIFIED user — no access token is issued until
  // the user clicks the verification link we email them. Do NOT setAuth here;
  // the Register page routes the user to /verify-email on success.
  return useMutation({
    mutationFn: (input: RegisterInput) => authApi.register(input),
    onSuccess: ({ user }) => {
      toast.success('Account created', `Welcome, ${user.name}! Check your email for the verification link.`);
    },
    onError: (err) => toast.error('Registration failed', getApiErrorMessage(err)),
  });
}

export function useVerifyEmail() {
  return useMutation({
    mutationFn: (input: { token: string }) => authApi.verifyEmail(input),
    onError: (err) => toast.error('Verification failed', getApiErrorMessage(err)),
  });
}

export function useResendVerification() {
  return useMutation({
    mutationFn: (input: { email: string }) => authApi.resendVerification(input),
    onSuccess: () => toast.success('Code sent', 'Check your inbox for a fresh code'),
    onError: (err) => toast.error('Could not resend', getApiErrorMessage(err)),
  });
}

export function useForgotPassword() {
  // Always resolves to a generic "sent" — the backend is intentionally silent
  // about whether the email exists, so we never reveal account existence here.
  return useMutation({
    mutationFn: (input: { email: string }) => authApi.forgotPassword(input),
    onError: (err) => toast.error('Could not send reset link', getApiErrorMessage(err)),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (input: { token: string; newPassword: string }) =>
      authApi.resetPassword(input),
    onError: (err) => toast.error('Reset failed', getApiErrorMessage(err)),
  });
}

export function useLogout() {
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => authApi.logout(),
    onSettled: () => {
      // Always clear local state, even if the server call fails — we still
      // want to drop the in-memory token. The HttpOnly cookie will expire
      // server-side on its own.
      clearAuth();
      queryClient.clear();
    },
    onSuccess: () => toast.info('Signed out'),
  });
}
